/**
 * SOLID Review
 * S — Strong. One concern: load one legal document by type — loading, its words or the error's — for the tabs that show one.
 * O — Good. A new document type is a new caller's input.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The view-model surface; nothing else.
 * D — Strong. One use case, the request contexts, the failure-message rule.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';

export interface IEntityLegalDocumentHookResult {
  readonly content: string | null;
  readonly loading: boolean;
  readonly error: string | null;
}

/** What a tab hands the loader: the document it shows, its words when the server lacks it, its words when the load fails. */
type TDocumentInputs = readonly [docType: string, fallbackText: string, errorFallbackText: string];

const LOADING: IEntityLegalDocumentHookResult = { content: null, loading: true, error: null };
const LOAD: TClientUseCaseInput<'loadLegalDocuments'> = {};

/**
 * ONE LEGAL DOCUMENT (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/useLegalDocument.ts`'s load effect, now a reaction
 * to the tab's inputs: loading while asked, the document's words (or the tab's fallback when the server lacks it), or the failure
 * in words the player can read. An answer that arrives after the tab left or changed its document is dropped.
 */
export class LegalDocumentLoader implements IServiceViewModel<IEntityLegalDocumentHookResult> {
  private readonly state = new ObservableState<IEntityLegalDocumentHookResult>(LOADING);
  private readonly reactions = new Reactions<TDocumentInputs>([{
    deps: (inputs) => inputs,
    run: ([docType, fallbackText, errorFallbackText]) => {
      let cancelled = false;
      const loading: Partial<IEntityLegalDocumentHookResult> = { loading: true };
      this.state.update(loading);
      void this.useCases.loadLegalDocuments(this.requests.context(), LOAD).then((loaded) => {
        if (cancelled) return;
        // One change, as the hook's two state updates were one render (React batches them).
        let answered: Partial<IEntityLegalDocumentHookResult>;
        if (!loaded.success) {
          answered = { error: messageFor(loaded.error, errorFallbackText), loading: false };
        } else {
          const doc = loaded.value.documents.find((d) => d.type === docType);
          answered = { content: doc?.content ?? fallbackText, loading: false };
        }
        this.state.update(answered);
      });
      return () => {
        cancelled = true;
      };
    },
  }]);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'loadLegalDocuments'>,
    private readonly requests: ClientRequests,
  ) {}

  getSnapshot(): IEntityLegalDocumentHookResult {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TDocumentInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TDocumentInputs);
  }
}
